import React from 'react';
import { Link } from 'react-router-dom';
import { colors } from '../data/content';
import { Home, AlertCircle } from 'lucide-react';

const NotFound = ({ language = 'ar' }) => {
  const isRTL = language === 'ar';

  const content = {
    en: {
      title: '404',
      subtitle: 'Page Not Found',
      description: 'Oops! The page you are looking for does not exist or has been moved.',
      button: 'Back to Home'
    },
    ar: {
      title: '404',
      subtitle: 'الصفحة غير موجودة',
      description: 'عذراً! الصفحة التي تبحث عنها غير موجودة أو تم نقلها.',
      button: 'العودة للرئيسية'
    }
  };

  const currentContent = content[language] || content.ar;

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: `linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 50%, #ffffff 100%)`,
      padding: '20px',
      direction: isRTL ? 'rtl' : 'ltr'
    }}>
      {/* Background Elements */}
      <div style={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        zIndex: 0
      }}>
        <div style={{
          position: 'absolute',
          top: '20%',
          left: '10%',
          width: '300px',
          height: '300px',
          background: `radial-gradient(circle, ${colors.primary}15 0%, transparent 70%)`,
          borderRadius: '50%',
          filter: 'blur(60px)'
        }}></div>
        <div style={{
          position: 'absolute',
          bottom: '20%',
          right: '10%',
          width: '400px',
          height: '400px',
          background: `radial-gradient(circle, ${colors.secondary}15 0%, transparent 70%)`,
          borderRadius: '50%',
          filter: 'blur(80px)'
        }}></div>
      </div>

      <div style={{
        textAlign: 'center',
        position: 'relative',
        zIndex: 10,
        maxWidth: '500px'
      }}>
        {/* Icon */}
        <div style={{
          width: '100px',
          height: '100px',
          borderRadius: '50%',
          background: `linear-gradient(135deg, ${colors.primary}20 0%, ${colors.secondary}20 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 24px',
          animation: 'pulse 2s ease-in-out infinite'
        }}>
          <AlertCircle size={50} color={colors.primary} />
        </div>

        {/* 404 Number */}
        <h1 style={{
          fontSize: 'clamp(5rem, 15vw, 8rem)',
          fontWeight: 900,
          background: `linear-gradient(135deg, ${colors.secondary} 0%, ${colors.primary} 100%)`,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          margin: '0',
          lineHeight: 1,
          fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif'
        }}>
          {currentContent.title}
        </h1>

        {/* Subtitle */}
        <h2 style={{
          fontSize: 'clamp(1.5rem, 4vw, 2rem)',
          fontWeight: 700,
          color: colors.secondary,
          margin: '16px 0',
          fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif'
        }}>
          {currentContent.subtitle}
        </h2>

        {/* Description */}
        <p style={{
          fontSize: 'clamp(1rem, 2.5vw, 1.125rem)',
          color: colors.text,
          margin: '0 0 32px',
          lineHeight: 1.7,
          fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif'
        }}>
          {currentContent.description}
        </p>

        {/* Home Button */}
        <Link
          to="/"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '10px',
            padding: '16px 32px',
            borderRadius: '16px',
            background: `linear-gradient(135deg, ${colors.primary} 0%, #9a0818 100%)`,
            color: 'white',
            fontWeight: 600,
            fontSize: '1.1rem',
            textDecoration: 'none',
            boxShadow: `0 8px 24px ${colors.primary}40`,
            transition: 'all 0.3s ease',
            fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
            e.currentTarget.style.boxShadow = `0 12px 32px ${colors.primary}50`;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0) scale(1)';
            e.currentTarget.style.boxShadow = `0 8px 24px ${colors.primary}40`;
          }}
        >
          <Home size={22} />
          <span>{currentContent.button}</span>
        </Link>
      </div>

      {/* Animation Styles */}
      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
      `}</style>
    </div>
  );
};

export default NotFound;
